import {
  Briefcase,
  Gift,
  Lightbulb,
  Question,
  SignOut,
  UserGear,
} from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { DropdownMenuHeader } from "common/components/ui/DropdownMenu/DropdownMenu";
import useCustomer from "common/datahooks/useCustomer";
import { getUserback } from "common/helpers/userbackClient";
import useWhitelabelCustomization from "common/hooks/useWhitelabelCustomization";

import LogOutModal from "../LogOutModal";

export default function ProfileDropdown({
  isExpanded,
}: {
  isExpanded: boolean;
}) {
  const { workspaceId } = useParams();
  const [isLogOutModalOpen, setIsLogOutModalOpen] = useState(false);

  const { whitelabelCustomization } = useWhitelabelCustomization();
  const { customerInfo } = useCustomer();
  const { first_name: firstName, last_name: lastName, email } = customerInfo;
  const fullName = `${firstName} ${lastName}`;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={clsx(
            "flex items-center gap-x-1.5 overflow-hidden justify-start border border-transparent data-[state=open]:border-black-100 transition-[background,border]",
            isExpanded
              ? "hover:bg-black-200 hover:border-black-200 data-[state=open]:bg-white p-2 mt-1 ml-1 mb-1.5 rounded-2xl"
              : "mb-3.5 ml-3 mr-2.5 mt-3 rounded-full",
          )}
          data-cy="profile-button"
        >
          <AimfoxAvatar firstName={firstName} lastName={lastName} size="sm" />

          <span
            className={clsx(
              "transition-opacity duration-500 w-full truncate text-left text-xs-bold",
              isExpanded ? "opacity-100" : "opacity-0",
            )}
          >
            {fullName}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="z-tooltip w-[211px]"
          side="right"
          align="end"
        >
          {/* Profile Info */}
          <DropdownMenuHeader className="flex items-center gap-x-1.5">
            <AimfoxAvatar firstName={firstName} lastName={lastName} size="sm" />

            <div className="flex flex-col items-start min-w-0">
              <span className="w-full truncate text-left text-xs-bold">
                {firstName} {lastName}
              </span>
              <span className="w-full truncate text-button-xxs text-black-500">
                {email}
              </span>
            </div>
          </DropdownMenuHeader>

          <DropdownMenuList>
            <RenderIf condition={!whitelabelCustomization}>
              <DropdownMenuItem asChild>
                <Link to={`/workspaces/${workspaceId}/settings`}>
                  <UserGear weight="fill" />
                  Profile settings
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <Link to={`/workspaces/${workspaceId}/settings/agency`}>
                  <Briefcase weight="fill" />
                  Agency plan
                </Link>
              </DropdownMenuItem>

              {/* Extra items */}
              {window.Cello && (
                <DropdownMenuItem
                  onClick={() => window.Cello("open")}
                  data-intercom-target="cello-button"
                >
                  <Gift weight="fill" />
                  Referral program
                </DropdownMenuItem>
              )}

              <DropdownMenuItem onClick={() => getUserback().openForm()}>
                <Lightbulb weight="fill" />
                Feedback & Roadmap
              </DropdownMenuItem>
            </RenderIf>
            <DropdownMenuItem asChild>
              <a
                href="https://help.aimfox.com/en/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Question weight="fill" />
                Knowledge base
              </a>
            </DropdownMenuItem>
          </DropdownMenuList>
          <DropdownMenuFooter>
            <DropdownMenuItem
              onClick={() => setIsLogOutModalOpen(true)}
              data-cy="log-out-button"
            >
              <SignOut />
              Log Out
            </DropdownMenuItem>
          </DropdownMenuFooter>
        </DropdownMenuContent>
      </DropdownMenu>

      <LogOutModal
        isOpen={isLogOutModalOpen}
        setIsOpen={setIsLogOutModalOpen}
      />
    </>
  );
}
